import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from './api';
import { trackPurchase } from './analytics';
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import BottomNav, { NAV_ICON } from './components/BottomNav';
import PicksPage from './pages/PicksPage';
import LeaderboardPage from './pages/LeaderboardPage';
import SeasonStandingsPage from './pages/SeasonStandingsPage';
import PoolsPage from './pages/PoolsPage';
import SurvivorPage from './pages/SurvivorPage';
import AdminPage from './pages/AdminPage';
import ScoreTicker from './components/ScoreTicker';
import RecapPage from './pages/RecapPage';
import { CountdownBadge } from './components/Countdown';
import InstallPrompt from './components/InstallPrompt';
import VersionCheck from './components/VersionCheck';
import SupportModal from './components/SupportModal';
import track, { trackLaunch } from './track';
import { useGames } from './gamesStore';

const styles = {
  app: { minHeight: '100vh', background: '#0a0e1a', color: '#fff' },
  nav: {
    background: 'linear-gradient(135deg, #013369 0%, #0a0e1a 100%)',
    borderBottom: '3px solid #D50A0A',
    padding: '0 12px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    height: 56, position: 'sticky', top: 0, zIndex: 100,
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    // The brand is fixed-width; without this the nav can still be widened by
    // its own contents even though the right-hand side clips.
    maxWidth: '100%', overflow: 'hidden'
  },
  navBrand: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700,
    color: '#fff', letterSpacing: 1, flexShrink: 0
  },
  // minWidth:0 lets this shrink instead of forcing the nav wider than the
  // window, and the clip means a tab count we haven't anticipated can never
  // push the whole page sideways again — the breakpoint decides what's
  // comfortable, this decides what's possible.
  navRight: { display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, overflow: 'hidden' },
  navTab: {
    padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
    fontWeight: 600, fontSize: 12, border: 'none',
    transition: 'all 0.2s', background: 'transparent', color: '#778',
    whiteSpace: 'nowrap'
  },
  navTabActive: { background: '#D50A0A', color: '#fff' },
  userBadge: {
    padding: '5px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.08)',
    fontSize: 12, fontWeight: 600, color: '#aab', marginLeft: 4, display: 'none'
  },
  logoutBtn: {
    padding: '5px 10px', borderRadius: 6, background: 'rgba(213,10,10,0.15)',
    border: '1px solid rgba(213,10,10,0.3)', color: '#ff8888',
    cursor: 'pointer', fontSize: 12, fontWeight: 600, marginLeft: 2
  },
  // In navRight rather than the tab strip, because the strip is hidden below
  // 960px and this is the one control that has to survive every width. It also
  // can't be a nav tab: the bottom bar is already seven items, and help is not
  // a place you go, it's something you reach for from wherever you got stuck.
  // Sized up rather than left to match the tabs around it. A single "?" glyph
  // makes a ~28px target, and this is the one control in the app that gets
  // pressed by someone who is already annoyed, quite possibly one-handed on a
  // phone. Missing it is the failure this whole feature exists to prevent.
  helpBtn: {
    minWidth: 36, height: 32, padding: '0 10px', borderRadius: 6,
    background: 'rgba(127,179,255,0.12)', border: '1px solid rgba(127,179,255,0.28)',
    color: '#7fb3ff', cursor: 'pointer', fontSize: 15, fontWeight: 700,
    marginLeft: 2, flexShrink: 0, lineHeight: 1
  }
};

// What did that link ask for?
//
// Notification deep links arrive as /?tab=<id>, and Stripe returns from Checkout
// as /?tab=pools&paid=1. Both live in the same query string, so it has to be
// parsed in one place: stripping it for the tab used to also throw away paid=1
// before the membership effect could ever see it, which silently killed the
// post-payment re-poll.
//
// Pure, and shared by two callers: the cold start below, and a notification
// tapped while the app is already running. That second case was unreachable
// before — this ran once at module load, so a warm app never looked at a link
// again and every deep link into a running app was silently dropped.
function readParams(search) {
  const out = { tab: null, paid: false, inviteCode: '', fromNotification: false, dmWith: null };
  try {
    const q = new URLSearchParams(search);
    const valid = ['home', 'picks', 'pools', 'leaderboard', 'season', 'recap', 'survivor', 'admin'];
    const t = q.get('tab');
    if (t && valid.includes(t)) out.tab = t;
    out.paid = q.get('paid') === '1';

    // Notifications deep-link with &n=<kind> purely so the app can tell an open
    // that came from a notification apart from one that didn't. Without it,
    // "does anyone actually come back when we nudge them" is unanswerable — and
    // that is the number that decides whether push is worth anything.
    out.fromNotification = !!q.get('n');

    // A DM notification links to the conversation it's about, not just the app.
    // Tapping a notification with someone's name on it and landing on a page
    // where you have to go find them is a strange thing to ask.
    const dm = parseInt(q.get('dm'), 10);
    if (Number.isInteger(dm) && dm > 0) { out.dmWith = dm; out.tab = 'home'; }

    // Invite emails link to /?code=ABC123 behind a "JOIN THE POOL" button. The
    // app never read the parameter, so that button just opened the home page and
    // the recipient had to read the code off the email and type it in — a poor
    // first thing to happen to somebody you invited. Carry it to the pools tab
    // and pre-fill it there.
    const code = (q.get('code') || '').trim().toUpperCase();
    if (/^[A-Z0-9]{4,12}$/.test(code)) {
      out.inviteCode = code;
      out.tab = 'pools';
    }

  } catch (e) {}
  return out;
}

// Read the launch URL ONCE at module load, before anything rewrites it.
const BOOT = (function readBootParams() {
  const out = readParams(window.location.search);
  if (!out.tab) out.tab = 'home';
  try {
    // Clear it so a refresh doesn't re-pin the tab, re-arm the poll, or offer an
    // invite they've already accepted.
    const q = new URLSearchParams(window.location.search);
    if (q.get('tab') || q.get('paid') || q.get('code') || q.get('n') || q.get('dm')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  } catch (e) {}
  return out;
})();

export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState(BOOT.tab);
  // Held in App, not in the pools page, so it survives signing in or registering
  // first — the invite may well be someone's very first visit.
  const [inviteCode, setInviteCode] = useState(BOOT.inviteCode);
  // {id, seq} rather than a bare id: the seq is what lets the same person's
  // second message reopen a thread you already closed.
  const [dmRequest, setDmRequest] = useState({ id: BOOT.dmWith, seq: 0 });
  const [pools, setPools] = useState([]);
  // "No pools" and "haven't asked yet" are both an empty array, and the boards
  // now render a "you're not in a pool" page for the first one. Without this
  // they'd render it for the second too — a member would get told they have no
  // pool for the moment before /pools/mine answers, on every cold load.
  const [poolsLoaded, setPoolsLoaded] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [dmUnread, setDmUnread] = useState({ total: 0, bySender: {} });
  const [weekMeta, setWeekMeta] = useState(null);
  const [membership, setMembership] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const selectedPoolRef = useRef(null);
  useEffect(() => { selectedPoolRef.current = selectedPool; }, [selectedPool]);

  useEffect(() => {
    const token = localStorage.getItem('nfl_token');
    const userData = localStorage.getItem('nfl_user');
    if (token && userData) {
      try { setUser(JSON.parse(userData)); } catch (e) {}
      API.get('/auth/me').then(res => {
        if (!res.data) return;
        setUser(prev => {
          const merged = { ...(prev || {}), ...res.data };
          localStorage.setItem('nfl_user', JSON.stringify(merged));
          return merged;
        });
      }).catch(() => {});
    }
  }, []);

  // Funnel: one launch per signed-in session, and whether it came from a
  // notification. Keyed on having a user rather than on mount, because a fresh
  // sign-in has no token at mount and the event would be dropped. The ref keeps
  // a re-render from counting the same launch twice.
  const launchCounted = useRef(false);
  useEffect(() => {
    if (!user || launchCounted.current) return;
    launchCounted.current = true;
    trackLaunch();
    if (BOOT.fromNotification) track('notification_opened');
  }, [user]);

  /**
   * A notification tapped while the app is already open.
   *
   * The service worker can't reliably navigate an installed iOS app — tapping
   * usually just brings it to the foreground — so it posts the target URL here
   * instead and we route in place. The `seq` counter matters: two messages from
   * the same person are the same dmWith value, and without something changing,
   * the effect that opens the thread would never re-run after you closed it.
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e) => {
      if (e.data?.type !== 'deeplink' || !e.data.url) return;
      let p;
      try { p = readParams(new URL(e.data.url, window.location.origin).search); }
      catch (err) { return; }
      if (p.tab) setTab(p.tab);
      if (p.inviteCode) setInviteCode(p.inviteCode);
      if (p.dmWith) setDmRequest(prev => ({ id: p.dmWith, seq: prev.seq + 1 }));
      if (p.fromNotification) track('notification_opened');
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const loadPools = useCallback(async () => {
    try {
      const res = await API.get('/pools/mine');
      const userPools = res.data.pools || [];
      setPools(userPools);
      const current = selectedPoolRef.current;
      if (!current && userPools.length > 0) {
        setSelectedPool(userPools[0]);
      } else if (current) {
        const updated = userPools.find(p => p.id === current.id);
        if (updated && (updated.name !== current.name || updated.invite_code !== current.invite_code || updated.member_count !== current.member_count)) {
          setSelectedPool(updated);
        } else if (!updated) {
          setSelectedPool(userPools[0] || null);
        }
      }
    } catch (e) {} finally { setPoolsLoaded(true); }
  }, []);

  useEffect(() => {
    if (user) {
      loadPools();
      const interval = setInterval(loadPools, 60000);
      return () => clearInterval(interval);
    }
  }, [user, loadPools]);

  const loadMembership = useCallback(async () => {
    try {
      const res = await API.get('/membership/me');
      setMembership(res.data || null);
    } catch (e) { /* leave as-is; the UI just won't show a paywall */ }
  }, []);

  // Refresh after returning from Stripe Checkout (?paid=1) so the gate lifts as
  // soon as the webhook lands — it can trail the browser redirect by a moment.
  const paidPollArmed = useRef(BOOT.paid);
  useEffect(() => {
    if (!user) return;
    loadMembership();
    if (!paidPollArmed.current) return;
    paidPollArmed.current = false; // only ever poll once per return from Checkout
    trackPurchase(); // Stripe only sends us back with ?paid=1 on success
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      loadMembership();
      if (n >= 5) clearInterval(iv);
    }, 2000);
    return () => clearInterval(iv);
  }, [user, loadMembership]);

  const loadDmUnread = useCallback(async () => {
    try {
      const res = await API.get('/dm/unread');
      setDmUnread({ total: res.data?.total || 0, bySender: res.data?.bySender || {} });
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!user) return;
    loadDmUnread();
    const iv = setInterval(loadDmUnread, 12000);
    return () => clearInterval(iv);
  }, [user, loadDmUnread]);

  // The countdown badge's week, from the shared copy. It used to run its own
  // five-minute poll for the same endpoint everything else was already asking
  // for — and a countdown that refreshes every five minutes is a countdown that
  // can be five minutes wrong at the moment it matters most.
  const sharedGames = useGames();
  useEffect(() => {
    if (sharedGames.loaded) setWeekMeta(sharedGames.week || null);
  }, [sharedGames]);

  const handleLogin = useCallback((userData, token) => {
    localStorage.setItem('nfl_token', token);
    localStorage.setItem('nfl_user', JSON.stringify(userData));
    setUser(userData);
    // Someone arriving on an invite link usually has to sign in or register
    // first. Dropping them on Home afterwards would lose the invite at the one
    // moment it matters, so send them where the code is waiting.
    setTab(inviteCode ? 'pools' : 'home');
  }, [inviteCode]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('nfl_token');
    localStorage.removeItem('nfl_user');
    setUser(null);
    setPools([]);
    setSelectedPool(null);
  }, []);

  const handleUserUpdate = useCallback((updates, newToken) => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...updates };
      localStorage.setItem('nfl_user', JSON.stringify(next));
      return next;
    });
    if (newToken) localStorage.setItem('nfl_token', newToken);
  }, []);

  const handleViewPool = (pool) => {
    setSelectedPool(pool);
    setTab('leaderboard');
  };

  if (!user) return <LoginPage onLogin={handleLogin} />;

  // Survivor is opt-in: only surface its tab once a pool you're in has it
  // enabled — or to commissioners/admins, who need the entry point to turn it
  // on. This keeps the nav uncluttered for pools that don't run it.
  const survivorVisible = !!(user.is_admin ||
    pools.some(p => p.survivor_enabled || p.commissioner_id === user.id));

  /* Icons come from BottomNav, which is the only place they are defined now.
     They were listed twice, once here for the desktop strip and once there for
     the phone, and the two had already drifted: Weekly and Recap both carried
     the clipboard here after Recap was given its own icon on the bar. Two lists
     of the same seven things will always drift, so there is one. */
  const tabs = [
    { id: 'home',        label: `${NAV_ICON.home} Home` },
    { id: 'picks',       label: `${NAV_ICON.picks} My Picks` },
    { id: 'pools',       label: `${NAV_ICON.pools} Pools` },
    { id: 'leaderboard', label: `${NAV_ICON.leaderboard} Weekly` },
    { id: 'season',      label: `${NAV_ICON.season} Season` },
    ...(survivorVisible ? [{ id: 'survivor', label: `${NAV_ICON.survivor} Survivor` }] : []),
    { id: 'recap',       label: `${NAV_ICON.recap} Recap` },
    ...(user.is_admin ? [{ id: 'admin', label: `${NAV_ICON.admin} Admin` }] : [])
  ];

  const poolProps = {
    pools, poolsLoaded, selectedPool,
    onSelectPool: (pool) => setSelectedPool(pool),
    onManagePools: () => setTab('pools')
  };

  return (
    <div style={styles.app}>
      <nav style={styles.nav}>
        <div style={styles.navBrand}>🏈 RIVALRY PICKS</div>
        <div style={styles.navRight}>
          <span className="desktop-nav-tabs" style={{ display: 'flex', gap: 2 }}>
            {tabs.map(t => (
              <button
                key={t.id}
                style={{ ...styles.navTab, ...(tab === t.id ? styles.navTabActive : {}) }}
                onClick={() => setTab(t.id)}
              >{t.label}</button>
            ))}
          </span>
          {weekMeta && !weekMeta.picks_locked && (
            <CountdownBadge lockAt={weekMeta.lock_at} isLocked={weekMeta.picks_locked} />
          )}
          {dmUnread.total > 0 && (
            <span
              title={dmUnread.total + ' unread DM' + (dmUnread.total === 1 ? '' : 's')}
              onClick={() => setTab('home')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '5px 10px', borderRadius: 14,
                background: 'linear-gradient(135deg, #D50A0A, #a00808)',
                color: '#fff', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', marginLeft: 4,
                animation: 'dmBadgePulse 2s ease-in-out infinite'
              }}
            >
              💬 {dmUnread.total > 99 ? '99+' : dmUnread.total}
            </span>
          )}
          <style>{`
            @keyframes dmBadgePulse {
              0%, 100% { box-shadow: 0 0 10px rgba(213,10,10,0.4); }
              50%      { box-shadow: 0 0 16px rgba(213,10,10,0.7); }
            }
          `}</style>
          <span className="user-badge" style={styles.userBadge}>👤 {user.username}</span>
          <button
            style={styles.helpBtn}
            onClick={() => setSupportOpen(true)}
            title="Something wrong? Message us"
          >?</button>
          <button style={styles.logoutBtn} onClick={handleLogout}>Out</button>
        </div>
      </nav>

      {/* Live score ticker — shows when games are loaded */}
      <ScoreTicker />

      <main>

        {/* membership reaches Home for the paywall notice. The picks page was
            the only place it appeared, which is the wrong surface for the one
            thing it now has to say before the deadline — "start your pool" is a
            decision someone makes on the home screen, not halfway through a
            sheet of picks they're already making. */}
        {tab === 'home'        && <HomePage user={user} pools={pools} poolsLoaded={poolsLoaded} selectedPool={selectedPool} membership={membership} onNavigate={setTab} onUserUpdate={handleUserUpdate} dmUnread={dmUnread} onDmRead={loadDmUnread} openDmWith={dmRequest} />}
        {tab === 'picks'       && <PicksPage user={user} membership={membership} onNavigate={setTab} />}
        {tab === 'pools'       && <PoolsPage user={user} onViewPool={handleViewPool} membership={membership} onMembershipChange={loadMembership}
                                             inviteCode={inviteCode} onInviteHandled={() => setInviteCode('')} />}
        {tab === 'leaderboard' && <LeaderboardPage user={user} {...poolProps} />}
        {tab === 'season'      && <SeasonStandingsPage user={user} {...poolProps} />}
        {tab === 'survivor'    && <SurvivorPage user={user} {...poolProps} />}
        {tab === 'recap'       && <RecapPage user={user} {...poolProps} />}
        {tab === 'admin'       && user.is_admin && <AdminPage user={user} />}
      </main>
      <BottomNav tab={tab} setTab={setTab} isAdmin={user.is_admin} showSurvivor={survivorVisible} homeBadge={dmUnread.total} />
      <InstallPrompt />
      {/* Tells a stale install that it's stale, and offers the one-tap fix. */}
      <VersionCheck />
      {supportOpen && <SupportModal me={user} onClose={() => setSupportOpen(false)} />}
    </div>
  );
}
